import React, { useState } from 'react';
import { useEquabotConfig } from '../hooks/useEquabotConfig';
import type { SettingsCategory } from '../services/gateway/settings';
import EquabotAuth from './EquabotAuth';

/**
 * Equabot Gateway Settings panel (Specs 086, 087).
 * Renders inside the EquanautSidebar when settings mode is active.
 */

interface EquabotSettingsProps {
    onBack?: () => void;
}

const TABS: { id: SettingsCategory; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'channels', label: 'Channels' },
    { id: 'models', label: 'Models' },
    { id: 'auth', label: 'Auth' },
];

const EquabotSettings: React.FC<EquabotSettingsProps> = ({ onBack }) => {
    const { config, loading, saving, error, gatewayConnected, gatewayVersion, save, refresh, setError } = useEquabotConfig();
    const [activeTab, setActiveTab] = useState<SettingsCategory>('general');

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        backgroundColor: '#1F313A',
    };

    const headerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        gap: '10px',
    };

    const backBtnStyle: React.CSSProperties = {
        background: 'none',
        border: 'none',
        color: '#8899A6',
        cursor: 'pointer',
        fontSize: '18px',
        padding: '4px 8px',
        borderRadius: '4px',
    };

    const titleStyle: React.CSSProperties = {
        flex: 1,
        fontSize: '15px',
        fontWeight: 600,
        color: '#E8F0FE',
    };

    const badgeStyle: React.CSSProperties = {
        fontSize: '10px',
        padding: '2px 8px',
        borderRadius: '10px',
        fontWeight: 600,
        backgroundColor: gatewayConnected ? 'rgba(51,170,64,0.2)' : 'rgba(255,86,86,0.2)',
        color: gatewayConnected ? '#4DC95A' : '#FF5656',
    };

    const tabBarStyle: React.CSSProperties = {
        display: 'flex',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        padding: '0 16px',
    };

    const tabStyle = (isActive: boolean): React.CSSProperties => ({
        padding: '8px 14px',
        fontSize: '12px',
        fontWeight: isActive ? 600 : 400,
        color: isActive ? '#4DC95A' : '#8899A6',
        background: 'none',
        border: 'none',
        borderBottom: isActive ? '2px solid #4DC95A' : '2px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    });

    const contentStyle: React.CSSProperties = {
        flex: 1,
        overflow: 'auto',
        padding: '16px',
    };

    const errorBannerStyle: React.CSSProperties = {
        padding: '8px 12px',
        backgroundColor: 'rgba(255,86,86,0.15)',
        color: '#FF8A8A',
        fontSize: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    };

    const fieldGroupStyle: React.CSSProperties = {
        marginBottom: '20px',
    };

    const labelStyle: React.CSSProperties = {
        display: 'block',
        fontSize: '11px',
        fontWeight: 600,
        color: '#8899A6',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: '6px',
    };

    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '8px 12px',
        fontSize: '13px',
        backgroundColor: '#2B3B44',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '6px',
        color: '#E8F0FE',
        outline: 'none',
        boxSizing: 'border-box' as const,
    };

    const toggleRowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    };

    const toggleLabelStyle: React.CSSProperties = {
        fontSize: '13px',
        color: '#C8D6DF',
    };

    const toggleStyle = (enabled: boolean): React.CSSProperties => ({
        width: '36px',
        height: '20px',
        borderRadius: '10px',
        backgroundColor: enabled ? '#33AA40' : 'rgba(255,255,255,0.15)',
        position: 'relative' as const,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        border: 'none',
        padding: 0,
    });

    const toggleDotStyle = (enabled: boolean): React.CSSProperties => ({
        position: 'absolute' as const,
        top: '2px',
        left: enabled ? '18px' : '2px',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s ease',
    });

    const saveBtnStyle: React.CSSProperties = {
        padding: '8px 20px',
        fontSize: '13px',
        fontWeight: 600,
        backgroundColor: saving ? '#2B3B44' : '#33AA40',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: saving ? 'not-allowed' : 'pointer',
        marginTop: '12px',
        opacity: saving ? 0.7 : 1,
    };

    const infoStyle: React.CSSProperties = {
        fontSize: '11px',
        color: '#6B7F8E',
        marginTop: '4px',
    };

    if (loading) {
        return (
            <div style={containerStyle}>
                <div style={{ ...contentStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8899A6' }}>
                    Loading configuration…
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                {onBack && (
                    <button style={backBtnStyle} onClick={onBack} title="Back to chat">
                        ←
                    </button>
                )}
                <span style={titleStyle}>Equabot Settings</span>
                <span style={badgeStyle}>
                    {gatewayConnected ? `● Connected${gatewayVersion ? ` v${gatewayVersion}` : ''}` : '○ Disconnected'}
                </span>
            </div>

            {/* Error banner */}
            {error && (
                <div style={errorBannerStyle}>
                    <span>{error}</span>
                    <button
                        style={{ ...backBtnStyle, fontSize: '14px', color: '#FF8A8A' }}
                        onClick={() => setError(null)}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div style={tabBarStyle}>
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        style={tabStyle(activeTab === tab.id)}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div style={contentStyle}>
                {activeTab === 'general' && (
                    <>
                        <div style={fieldGroupStyle}>
                            <label style={labelStyle}>Gateway Status</label>
                            <div style={{ ...toggleRowStyle, borderBottom: 'none' }}>
                                <span style={toggleLabelStyle}>Orchestration Enabled</span>
                                <button
                                    style={toggleStyle(config?.orchestration?.enabled ?? false)}
                                    onClick={() => save({ orchestration: { ...config?.orchestration!, enabled: !config?.orchestration?.enabled } })}
                                >
                                    <div style={toggleDotStyle(config?.orchestration?.enabled ?? false)} />
                                </button>
                            </div>
                            <p style={infoStyle}>
                                When enabled, the Gateway handles agent orchestration, task routing, and multi-channel coordination.
                            </p>
                        </div>
                        <div style={fieldGroupStyle}>
                            <label style={labelStyle}>Max Concurrent Agents</label>
                            <input
                                style={inputStyle}
                                type="number"
                                min={1}
                                max={10}
                                value={config?.orchestration?.maxConcurrentAgents ?? 3}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    if (!isNaN(val) && val >= 1 && val <= 10) {
                                        save({ orchestration: { ...config?.orchestration!, maxConcurrentAgents: val } });
                                    }
                                }}
                            />
                        </div>
                        <button style={saveBtnStyle} onClick={refresh} disabled={saving}>
                            {saving ? 'Saving…' : '↻ Refresh'}
                        </button>
                    </>
                )}

                {activeTab === 'channels' && (
                    <>
                        {(['telegram', 'whatsapp', 'slack'] as const).map((channel) => (
                            <div key={channel} style={fieldGroupStyle}>
                                <div style={toggleRowStyle}>
                                    <span style={toggleLabelStyle}>
                                        {channel.charAt(0).toUpperCase() + channel.slice(1)}
                                    </span>
                                    <button
                                        style={toggleStyle(config?.channels?.[channel]?.enabled ?? false)}
                                        onClick={() => {
                                            const current = config?.channels?.[channel] ?? { enabled: false };
                                            save({
                                                channels: {
                                                    ...config?.channels!,
                                                    [channel]: { ...current, enabled: !current.enabled },
                                                },
                                            });
                                        }}
                                    >
                                        <div style={toggleDotStyle(config?.channels?.[channel]?.enabled ?? false)} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </>
                )}

                {activeTab === 'models' && (
                    <>
                        <div style={fieldGroupStyle}>
                            <label style={labelStyle}>Primary Model</label>
                            <input
                                style={inputStyle}
                                type="text"
                                value={config?.model ?? ''}
                                onChange={(e) => save({ model: e.target.value })}
                            />
                            <p style={infoStyle}>
                                e.g. claude-sonnet-4-20250514, gemini-2.5-pro
                            </p>
                        </div>
                        <div style={fieldGroupStyle}>
                            <label style={labelStyle}>Max Tokens</label>
                            <input
                                style={inputStyle}
                                type="number"
                                min={1024}
                                max={200000}
                                value={config?.maxTokens ?? 4096}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    if (!isNaN(val)) save({ maxTokens: val });
                                }}
                            />
                        </div>
                    </>
                )}

                {activeTab === 'auth' && (
                    <EquabotAuth
                        token={config?.auth?.gatewayToken}
                        tokenExpiry={config?.auth?.tokenExpiry}
                        onSaveToken={(token: string) => save({ auth: { ...config?.auth!, gatewayToken: token } })}
                    />
                )}
            </div>
        </div>
    );
};

export default EquabotSettings;
